import { CustomerOrderBase, LegacyCustomerOrderBase } from '../contracts/customer-order.js';
import { generateUniqueDatabaseId } from '../models/dto/dto.js';
import { Currency } from '../models/serializable/currency.js';
import { EventTicket } from '../models/serializable/event-ticket.js';
import { EventTicketOrder } from '../models/serializable/event-ticket-order.js';
import { InvoiceProvider } from '../models/serializable/invoicing/invoice-provider.js';
import { PaymentProvider } from '../models/serializable/payment-provider.js';
import { Price } from '../models/serializable/price.js';
import { TicketSupplement } from '../models/serializable/ticket-supplement.js';
import { IPaymentHelperConfig } from './core/payment-helper-config.js';
import { LegacyPaymentHelper } from './legacy-payment-helper.js';
import { GenericOrder } from '../models/serializable/orders/generic-order.js';
import { GenericPaymentHelper } from './generic-payment-helper.js';

const PAYMENT_ID_LENGTH: number = 28;

export class PaymentHelper {
  private constructor() {}

  static getUniquePaymentRequestId(): string {
    return generateUniqueDatabaseId({ length: PAYMENT_ID_LENGTH });
  }

  static getCurrencyOfOrder(order: CustomerOrderBase): Currency | null {
    if(LegacyCustomerOrderBase.assertType(order)) {
      return LegacyPaymentHelper.getCurrencyOfOrder(order);
    }
    else if(GenericOrder.assertType(order)) {
      return GenericPaymentHelper.getCurrencyOfOrder(order);
    }

    return null;
  }

  static calculateCheckoutPriceForOrder(order: CustomerOrderBase, config?: IPaymentHelperConfig): Price | null {
    if(LegacyCustomerOrderBase.assertType(order)) {
      return LegacyPaymentHelper.calculateCheckoutPriceForOrder(order, config);
    }
    else if(GenericOrder.assertType(order)) {
      return GenericPaymentHelper.calculateCheckoutPriceForOrder(order);
    }

    return null;
  }

  static calculateCheckoutPriceForTicketSupplement(supplement: TicketSupplement): Price {
    return LegacyPaymentHelper.calculateCheckoutPriceForTicketSupplement(supplement);
  }

  static calculateCheckoutPriceForEventTicket(ticket: EventTicket, options: { applySupplement: boolean }): Price {
    return LegacyPaymentHelper.calculateCheckoutPriceForEventTicket(ticket, options);
  }

  static calculateIncomeForEventTicket(ticket: EventTicket, options: { applySupplement: boolean }): Price {
    return LegacyPaymentHelper.calculateIncomeForEventTicket(ticket, options);
  }

  static calculateIncomeWithoutDiscountForEventTicket(ticket: EventTicket, options: { applySupplement: boolean }): Price {
    return LegacyPaymentHelper.calculateIncomeWithoutDiscountForEventTicket(ticket, options);
  }

  static calculateOriginalIncomeForOfflineTicketOrder(order: EventTicketOrder, options: {
    applyProducts: boolean,
    applySupplements: boolean,
  }): Price | null {
    return LegacyPaymentHelper.calculateOriginalIncomeForOfflineTicketOrder(order, options);
  }

  static encodePriceForTransaction({ price, provider }: { price: Price, provider: PaymentProvider }): number {
    switch(provider) {
      case PaymentProvider.Barion: {
        switch(price.currency) {
          case Currency.HUF:
            return price.convertToInt;
          case Currency.RON:
            // fallthrough
          case Currency.USD:
            // fallthrough
          case Currency.EUR:
            return price.convertToDecimal;
        }
      } break;
      case PaymentProvider.Stripe: {
        return price.convertToCents;
      }
      case PaymentProvider.BtPay: {
        return price.convertToCents;
      }
      case PaymentProvider.InTime: {
        return price.convertToCents;
      }
    }
  }

  static encodePriceForInvoice({ price, provider }: { price: Price, provider: InvoiceProvider }): number {
    switch(provider) {
      case InvoiceProvider.Billingo: {
        switch(price.currency) {
          case Currency.HUF:
            return price.convertToInt;
          case Currency.RON:
          // fallthrough
          case Currency.USD:
          // fallthrough
          case Currency.EUR:
            return price.convertToDecimal;
        }
      } break;
      case InvoiceProvider.Szamlazz: {
        return price.convertToDecimal;
      } break;
    }
  }
}
